import { Typography } from "@/components/shared/typography";
import { Image, View } from "@/components/ui";
import { useState } from "react";
import { MediaPreviewInput } from "@/components/shared/media-preview-input";
import { IconButton } from "@/components/ui/icon-button";
import { Play } from "@/components/shared/icons";
import type { MediaFile } from "@/components/shared/media-preview-input/types";

const MAX_ATTACHMENTS = 5;

type Attachment = {
  file: File;
  uri: string;
  isVideo: boolean;
};

export type AttachmentsData = {
  photos: File[];
  videos: File[];
};

export function AttachmentsInput({
  onChange,
}: {
  onChange(data: AttachmentsData): void;
}) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const handleFileChange = ({ file, uri, isVideo }: MediaFile) => {
    const newAttachments = [...attachments, { file, uri, isVideo }];
    setAttachments(newAttachments);
    onChange({
      photos: newAttachments.filter((a) => !a.isVideo).map((a) => a.file),
      videos: newAttachments.filter((a) => a.isVideo).map((a) => a.file),
    });
  };

  const handleRemoveAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(newAttachments);
    onChange({
      photos: newAttachments.filter((a) => !a.isVideo).map((a) => a.file),
      videos: newAttachments.filter((a) => a.isVideo).map((a) => a.file),
    });
  };

  return (
    <View className="flex flex-col gap-2">
      <View>
        <Typography variant="label-sm">Attachments (Optional)</Typography>
        <Typography variant="body-sm" className="color-content-secondary">
          Supporting documents such as photos or videos.
        </Typography>
      </View>
      <View className="flex flex-row flex-wrap gap-2">
        {attachments.map((attachment, index) => (
          <View key={attachment.uri} className="relative w-16 md:w-20">
            <IconButton
              size="xs"
              className="absolute -right-1 -top-1 z-10"
              iconName="x"
              variant="primary"
              color="danger"
              onClick={() => handleRemoveAttachment(index)}
            />
            {attachment.isVideo ? (
              <View className="aspect-square overflow-hidden rounded-md bg-black">
                <View className="flex h-full items-center justify-center">
                  <View className="rounded-full bg-white/80 p-1">
                    <Play
                      className="color-content-primary size-4"
                      weight="fill"
                    />
                  </View>
                </View>
              </View>
            ) : (
              <Image
                src={attachment.uri}
                className="aspect-square overflow-hidden rounded-md object-cover"
                alt={`attachment ${index + 1}`}
                contentFit="cover"
                style={{
                  height: undefined,
                  width: "100%",
                  aspectRatio: 1,
                }}
              />
            )}
          </View>
        ))}
        {attachments.length < MAX_ATTACHMENTS && (
          <View className="w-16 md:w-20">
            <MediaPreviewInput onChange={handleFileChange} />
          </View>
        )}
      </View>
    </View>
  );
}
